/**
 * HelpArticlePage — /help/:slug
 *
 * Renders a single help article from the typed block array.
 * Each block maps to a semantic HTML element for clean SEO output.
 * Includes a "Was this helpful?" feedback widget and a related articles list.
 */
import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { SEOHead } from "@/components/seo/SEOHead.tsx";
import { feedbackApi } from "@/api/feedback.ts";
import { articleJsonLd } from "@/components/seo/SEOHead.tsx";
import {
  getArticleBySlug,
  getRelatedArticles,
  HELP_CATEGORIES,
  type ArticleBlock,
  type HelpArticle,
} from "@/help/articles.ts";

const T = {
  ink: "#0a0a0a",
  inkSoft: "#6b6b6b",
  rule: "#e9e6df",
  paper: "#faf8f3",
  paper2: "#f3efe5",
  accent: "#ff5b1f",
  tipBg: "#fff7f4",
  tipBorder: "#ffddd0",
  warnBg: "#fffbea",
  warnBorder: "#ffe88a",
};

const PLAN_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  free:    { bg: "#f3efe5", text: "#6b6b6b", label: "Free" },
  starter: { bg: "#e8f4fd", text: "#1a6fa8", label: "Starter" },
  pro:     { bg: "#eef2ff", text: "#4f46e5", label: "Pro" },
  agency:  { bg: "#f0fdf4", text: "#16a34a", label: "Agency" },
};

// ── Block renderer ────────────────────────────────────────────────────────────

function RenderBlock({ block }: { block: ArticleBlock }) {
  switch (block.type) {
    case "p":
      return <p style={{ margin: "0 0 18px", fontSize: 15, lineHeight: 1.75, color: T.ink }}>{block.text}</p>;

    case "h2":
      return (
        <h2
          style={{ margin: "36px 0 12px", fontSize: 19, fontWeight: 700, letterSpacing: "-0.02em", color: T.ink, borderBottom: `1px solid ${T.rule}`, paddingBottom: 10 }}
        >
          {block.text}
        </h2>
      );

    case "h3":
      return (
        <h3 style={{ margin: "24px 0 10px", fontSize: 16, fontWeight: 700, color: T.ink }}>
          {block.text}
        </h3>
      );

    case "ul":
      return (
        <ul style={{ margin: "0 0 18px", paddingLeft: 24 }}>
          {block.items.map((item, i) => (
            <li key={i} style={{ fontSize: 15, lineHeight: 1.7, color: T.ink, marginBottom: 6 }}>{item}</li>
          ))}
        </ul>
      );

    case "ol":
      return (
        <ol style={{ margin: "0 0 18px", paddingLeft: 24 }}>
          {block.items.map((item, i) => (
            <li key={i} style={{ fontSize: 15, lineHeight: 1.7, color: T.ink, marginBottom: 6 }}>{item}</li>
          ))}
        </ol>
      );

    case "tip":
      return (
        <div
          role="note"
          style={{ margin: "20px 0", padding: "14px 16px", borderRadius: 10, background: T.tipBg, border: `1px solid ${T.tipBorder}`, display: "flex", gap: 12, alignItems: "flex-start" }}
        >
          <span aria-hidden="true" style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>💡</span>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: T.ink }}>{block.text}</p>
        </div>
      );

    case "warn":
      return (
        <div
          role="note"
          style={{ margin: "20px 0", padding: "14px 16px", borderRadius: 10, background: T.warnBg, border: `1px solid ${T.warnBorder}`, display: "flex", gap: 12, alignItems: "flex-start" }}
        >
          <span aria-hidden="true" style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>⚠️</span>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: T.ink }}>{block.text}</p>
        </div>
      );

    case "plan":
      return (
        <div
          style={{ margin: "20px 0", padding: "14px 16px", borderRadius: 10, background: T.paper2, border: `1px solid ${T.rule}`, display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}
        >
          <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
            {block.tiers.map((tier) => {
              const c = PLAN_COLORS[tier];
              return (
                <span key={tier} style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: c.bg, color: c.text }}>
                  {c.label}
                </span>
              );
            })}
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: T.inkSoft }}>{block.text}</p>
        </div>
      );

    case "steps":
      return (
        <ol role="list" style={{ margin: "0 0 18px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
          {block.items.map((step, i) => (
            <li key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <span
                style={{ flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: T.accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, marginTop: 2 }}
              >
                {i + 1}
              </span>
              <div>
                <p style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: T.ink }}>{step.title}</p>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: T.inkSoft }}>{step.text}</p>
              </div>
            </li>
          ))}
        </ol>
      );

    default:
      return null;
  }
}

// ── Feedback widget ───────────────────────────────────────────────────────────

function FeedbackWidget({ articleSlug }: { articleSlug: string }) {
  const [voted, setVoted] = useState<"yes" | "no" | null>(null);

  const submit = useMutation({
    mutationFn: (helpful: boolean) =>
      feedbackApi.submit({
        score: helpful ? 10 : 0,
        comment: "",
        trigger: `help_article_${articleSlug}`,
      }),
    onSuccess: (_data, helpful) => setVoted(helpful ? "yes" : "no"),
  });

  if (voted) {
    return (
      <div style={{ marginTop: 48, padding: "24px", borderRadius: 12, border: `1px solid ${T.rule}`, background: T.paper2, textAlign: "center" }}>
        <p style={{ margin: 0, fontSize: 15, color: T.inkSoft }}>
          {voted === "yes" ? "👍 Thanks for the feedback!" : "👎 Thanks — we'll work on improving this article."}
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 48, padding: "24px", borderRadius: 12, border: `1px solid ${T.rule}`, background: T.paper2, textAlign: "center" }}>
      <p style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600, color: T.ink }}>
        Was this article helpful?
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button
          onClick={() => submit.mutate(true)}
          disabled={submit.isPending}
          style={{ padding: "8px 20px", borderRadius: 8, border: `1px solid ${T.rule}`, background: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", color: T.ink, fontFamily: "inherit", transition: "border-color 0.15s" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.accent; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.rule; }}
        >
          👍 Yes
        </button>
        <button
          onClick={() => submit.mutate(false)}
          disabled={submit.isPending}
          style={{ padding: "8px 20px", borderRadius: 8, border: `1px solid ${T.rule}`, background: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", color: T.ink, fontFamily: "inherit", transition: "border-color 0.15s" }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.accent; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.rule; }}
        >
          👎 No
        </button>
      </div>
    </div>
  );
}

// ── Related articles sidebar ──────────────────────────────────────────────────

function RelatedArticles({ article }: { article: HelpArticle }) {
  const related = getRelatedArticles(article, 5);
  if (related.length === 0) return null;

  return (
    <aside>
      <h3 style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.inkSoft, marginBottom: 14 }}>
        Related articles
      </h3>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        {related.map((a) => (
          <li key={a.slug}>
            <Link
              to={`/help/${a.slug}`}
              style={{ display: "block", padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.rule}`, textDecoration: "none", background: "#fff", color: T.ink, fontSize: 13, fontWeight: 500, lineHeight: 1.4, transition: "border-color 0.15s" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = T.accent; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = T.rule; }}
            >
              {a.title}
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function HelpArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return <Navigate to="/help" replace />;
  }

  const category = HELP_CATEGORIES.find((c) => c.slug === article.category);
  const siteUrl = (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) || "https://tubeforge.io";

  function breadcrumbJsonLd() {
    const a = article!;
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Help",
          item: `${siteUrl}/help`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: category?.label ?? a.category,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: a.title,
          item: `${siteUrl}/help/${a.slug}`,
        },
      ],
    };
  }

  return (
    <>
      <SEOHead
        title={`${article.title} — Help`}
        description={article.description}
        path={`/help/${article.slug}`}
        ogType="article"
        article={{ publishedTime: article.publishedAt, modifiedTime: article.modifiedAt ?? article.publishedAt }}
        jsonLd={[
          articleJsonLd({
            title: article.title,
            description: article.description,
            url: `${siteUrl}/help/${article.slug}`,
            image: `${siteUrl}/og-default.png`,
            publishedTime: article.publishedAt,
            modifiedTime: article.modifiedAt ?? article.publishedAt,
          }),
          breadcrumbJsonLd(),
        ]}
      />

      {/* Breadcrumb */}
      <div style={{ borderBottom: `1px solid ${T.rule}`, background: T.paper2 }}>
        <div style={{ maxWidth: 1040, margin: "0 auto", padding: "14px 20px" }}>
          <nav aria-label="Breadcrumb">
            <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <li>
                <Link to="/help" style={{ fontSize: 13, color: T.inkSoft, textDecoration: "none" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = T.accent; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = T.inkSoft; }}
                >
                  Help
                </Link>
              </li>
              <li aria-hidden="true" style={{ fontSize: 13, color: T.rule }}>›</li>
              <li>
                <span style={{ fontSize: 13, color: T.inkSoft }}>{category?.label ?? article.category}</span>
              </li>
              <li aria-hidden="true" style={{ fontSize: 13, color: T.rule }}>›</li>
              <li aria-current="page">
                <span style={{ fontSize: 13, color: T.ink, fontWeight: 600 }}>{article.title}</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content area */}
      <div style={{ maxWidth: 1040, margin: "0 auto", padding: "40px 20px 80px", display: "grid", gridTemplateColumns: "1fr 260px", gap: 48 }}
        className="help-article-grid"
      >
        {/* Article body */}
        <article>
          {/* Header */}
          <header style={{ marginBottom: 32 }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.accent }}>
              {category?.label}
            </span>
            <h1
              style={{ margin: "8px 0 12px", fontSize: "clamp(22px, 4vw, 30px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.2, fontFamily: '"Bricolage Grotesque", "Inter", system-ui', color: T.ink }}
            >
              {article.title}
            </h1>
            <p style={{ margin: 0, fontSize: 15, color: T.inkSoft, lineHeight: 1.6 }}>
              {article.description}
            </p>
          </header>

          {/* Body blocks */}
          <div>
            {article.body.map((block, i) => (
              <RenderBlock key={i} block={block} />
            ))}
          </div>

          {/* Feedback */}
          <FeedbackWidget articleSlug={article.slug} />

          {/* Back link */}
          <div style={{ marginTop: 32 }}>
            <Link
              to="/help"
              style={{ fontSize: 13, color: T.inkSoft, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = T.accent; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = T.inkSoft; }}
            >
              ← Back to Help Center
            </Link>
          </div>
        </article>

        {/* Sidebar */}
        <div style={{ paddingTop: 4 }}>
          <RelatedArticles article={article} />
        </div>
      </div>

      {/* Responsive: collapse sidebar below article on small screens */}
      <style>{`
        @media (max-width: 720px) {
          .help-article-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}
